import { useState } from "react";
import { devNotesReview, devNotesUpdate } from "@/lib/devActionableNotesClient";
import type { DevActionableNote } from "./devActionableNoteTypes";
import { notePlacement, noteSyncBody } from "./devActionableNoteTypes";

type Props = {
  note: DevActionableNote;
  isOwner: boolean;
  canEditBody: boolean;
  getToken: () => Promise<string | null>;
  savingId: string | null;
  setSavingId: (id: string | null) => void;
  onError: (msg: string) => void;
  onDone: () => void;
  disabled?: boolean;
};

export function DevPanelActionableNotesReadyEdit({
  note,
  isOwner,
  canEditBody,
  getToken,
  savingId,
  setSavingId,
  onError,
  onDone,
  disabled = false,
}: Props) {
  const [editOpen, setEditOpen] = useState(false);
  const [editBody, setEditBody] = useState("");
  const [repoPath, setRepoPath] = useState("");

  const needsReviewApproved =
    note.trust_tier === "needs_review" &&
    (note.review_status === "approved" || note.review_status === "approved_altered");

  if (!canEditBody && !(isOwner && needsReviewApproved)) return null;

  function openEdit() {
    if (needsReviewApproved) {
      setEditBody(noteSyncBody(note));
    } else {
      setEditBody(note.body);
      setRepoPath(notePlacement(note)?.repo_path ?? "");
    }
    setEditOpen(true);
  }

  async function saveApprovedAlter() {
    const token = await getToken();
    const effective_body = editBody.trim();
    if (!token || !effective_body) return;
    setSavingId(note.id);
    const result = await devNotesReview(token, { id: note.id, action: "alter", effective_body });
    setSavingId(null);
    if (!result.ok) {
      onError(result.error);
      return;
    }
    setEditOpen(false);
    onDone();
  }

  async function saveOwnerDirectEdit() {
    const token = await getToken();
    const body = editBody.trim();
    if (!token || !body) return;
    setSavingId(note.id);
    const path = repoPath.trim();
    const placement = path ? { repo_path: path } : undefined;
    const result = await devNotesUpdate(token, { id: note.id, body, placement });
    setSavingId(null);
    if (!result.ok) {
      onError(result.error);
      return;
    }
    setEditOpen(false);
    onDone();
  }

  async function reopenToPending() {
    if (
      !window.confirm(
        "Return this to pending review? It will leave the export queue until you approve it again.",
      )
    ) {
      return;
    }
    const token = await getToken();
    if (!token) return;
    setSavingId(note.id);
    const result = await devNotesReview(token, { id: note.id, action: "reopen" });
    setSavingId(null);
    if (!result.ok) {
      onError(result.error);
      return;
    }
    onDone();
  }

  const busy = disabled || savingId === note.id;

  if (editOpen) {
    return (
      <div className="space-y-2 rounded-lg border border-emerald-100 bg-emerald-50/50 p-2">
        <p className="text-[10px] font-medium uppercase text-emerald-900">
          {needsReviewApproved ? "Edit sync prompt" : "Edit note"}
        </p>
        <textarea
          className="w-full rounded border border-emerald-200 px-2 py-1.5 text-sm"
          rows={4}
          value={editBody}
          onChange={(e) => setEditBody(e.target.value)}
        />
        {canEditBody && !needsReviewApproved ? (
          <label className="block text-[11px] text-gray-600">
            Target path (public/… or src/assets/…)
            <input
              className="mt-0.5 w-full rounded border border-gray-200 px-2 py-1 text-xs font-mono"
              value={repoPath}
              onChange={(e) => setRepoPath(e.target.value)}
              placeholder="public/ui/example.svg"
            />
          </label>
        ) : null}
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="rounded-lg bg-emerald-800 px-3 py-1 text-xs font-semibold text-white disabled:opacity-50"
            disabled={busy || !editBody.trim()}
            onClick={() => void (needsReviewApproved ? saveApprovedAlter() : saveOwnerDirectEdit())}
          >
            Save
          </button>
          <button
            type="button"
            className="rounded-lg border border-gray-300 px-3 py-1 text-xs"
            onClick={() => setEditOpen(false)}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-2">
      <button
        type="button"
        className="rounded-lg border border-emerald-400 bg-white px-3 py-1 text-xs font-semibold text-emerald-900 disabled:opacity-50"
        disabled={busy}
        onClick={() => openEdit()}
      >
        {needsReviewApproved ? "Edit prompt" : "Edit"}
      </button>
      {isOwner && needsReviewApproved ? (
        <button
          type="button"
          className="rounded-lg border border-amber-400 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-900 disabled:opacity-50"
          disabled={busy}
          onClick={() => void reopenToPending()}
        >
          Return to pending
        </button>
      ) : null}
    </div>
  );
}
